import type {
  CatalogList,
  LiberacaoDeMensagem,
  MensagemRetida,
  MensagemRetidaDetalhe,
  ResumoDaFilaDeRetidas,
  SolicitantePossivel,
} from '@central/contracts';
import { get, post } from '@/lib/api';

/**
 * A fila de mensagens retidas (`/email/held/*`).
 *
 * Uma função por rota do servidor, sem nenhuma inventada, e os tipos vindos de
 * `@central/contracts` — a API e a tela leem a mesma declaração.
 *
 * Note o que **não** existe: `apagarMensagem`. Não é esquecimento — o servidor
 * não tem `DELETE` nessa rota. A mensagem é a prova de que o cliente escreveu;
 * descartar marca e tira da fila, e é `descartarMensagem`.
 */

export interface FiltroDeRetidas {
  q?: string;
}

export function listarRetidas(filtro: FiltroDeRetidas = {}) {
  const busca = new URLSearchParams();
  if (filtro.q) busca.set('q', filtro.q);
  const consulta = busca.toString();
  return get<CatalogList<MensagemRetida>>(`/email/held${consulta ? `?${consulta}` : ''}`);
}

export const obterRetida = (id: string) => get<MensagemRetidaDetalhe>(`/email/held/${id}`);

export const resumoDasRetidas = () => get<ResumoDaFilaDeRetidas>('/email/held/summary');

/**
 * Libera — abre o chamado.
 *
 * O e-mail do solicitante **não** entra no corpo: ele vem da mensagem gravada,
 * no servidor. O tipo diz a mesma coisa que a API, para o erro aparecer na
 * compilação em vez de num 400 na tela.
 */
export type Liberacao =
  | { modo: 'NOVO_CONTATO'; accountId: string; name: string; jobTitle?: string; phone?: string }
  | { modo: 'SOLICITANTE_EXISTENTE'; requesterId: string };

export const liberarMensagem = (id: string, corpo: Liberacao) =>
  post<LiberacaoDeMensagem>(`/email/held/${id}/release`, corpo);

export const descartarMensagem = (id: string, motivo: string) =>
  post<MensagemRetida>(`/email/held/${id}/discard`, { motivo });

/**
 * Candidatos a solicitante.
 *
 * Rota própria, e não `/lookups/people/search`: aquela devolve só pessoas
 * **internas**, porque serve ao campo de atribuição. Aqui o solicitante quase
 * sempre é o contato do cliente.
 */
export const buscarSolicitantes = (q: string) =>
  get<{ items: SolicitantePossivel[] }>(`/email/held/candidates?q=${encodeURIComponent(q)}`);

// ── chaves do TanStack Query ───────────────────────────────────────────────

/**
 * Uma raiz só, e invalidação pela raiz depois de decidir.
 *
 * Liberar ou descartar muda a lista, muda o contador do painel e some com a
 * mensagem aberta. Enumerar cada chave afetada seria uma lista para esquecer de
 * atualizar — e o sintoma seria o pior possível: o contador continuar acusando
 * uma mensagem que já foi resolvida.
 */
export const chavesDeRetidas = {
  raiz: ['retidas'] as const,
  lista: (filtro: FiltroDeRetidas = {}) => ['retidas', 'lista', filtro.q ?? ''] as const,
  detalhe: (id: string) => ['retidas', 'detalhe', id] as const,
  resumo: ['retidas', 'resumo'] as const,
};
